export enum AssetType {
  Unknown = 'unknown',
  Tweet = 'tweet',
  TweetPoll = 'tweetpoll', // Same as AssetGroupType (for legacy reasons)
  Instagram = 'igpost',
  Graphic = 'graphic',
  Video = 'video',
  Stinger = 'stinger',
  Widget = 'widget',
  SimpleCG = 'simplecg',
  Caster = 'caster',
  Broadcast = 'broadcast',
  Chat = 'chat',
  YouTubeChat = 'yt_chat',
  FacebookChat = 'fb_chat',
  Playlist = 'playlist'
}

export enum AssetGroupType {
  Unknown = 'unknown',
  Folder = 'folder',
  RootFolder = '',
  TwitterHashtag = 'hashtag',
  TwitterHandle = 'handle',
  Tweet = 'tweet', // Single tweet
  TwitterPoll = 'tweetpoll', // Same as AssetType (for legacy reasons)
  InstagramHashtag = 'ig_hashtag',
  InstagramHandle = 'ig_handle',
  YouTubeChat = 'yt_chat',
  FacebookChat = 'fb_chat',
  LiveStream = 'livestreams',
  Widget = 'widget'
}

export interface AssetFolderQuery {
  assetGroupId: string,
  minStars: number,
  teamId: string
}

export enum AssetGroupErrors {
  FolderNotEmpty = 'FolderNotEmpty'
}

export const SocialMediaTypes = [
  AssetGroupType.TwitterHashtag,
  AssetGroupType.TwitterHandle,
  AssetGroupType.TwitterPoll,
  AssetGroupType.InstagramHashtag,
  AssetGroupType.YouTubeChat,
  AssetGroupType.FacebookChat,
] as string[]

export const SocialMediaAssetTypes = [
  AssetType.TweetPoll,
  AssetType.Tweet,
  AssetType.Instagram
] as string[]

export class Asset {
  type: AssetType = AssetType.Unknown
  asset_group: string = ''
  deleted: boolean = false
  id: string = ''
  data?: any
  url?: string
  // box?: string | undefined // box property is only used by sceneContent items
  name: string = ''
  stars: number = 0
  thumbnail?: string
  original_url?: string // our thumbnails don't have transparency
  team_id?: string
  user_id?: string
  timestamp?: number
  transitionDelay?: number
  actionOffset?: number
  order: number = 0
}

export interface AssetSummary {
  id: string,
  name?: string,
  url?: string,
  type: AssetType,
  timestamp?: number,
  stars: number,
  state?: ClipTranscodingState
}

export interface TweetAsset extends Asset {
  tweet_id?: string,
  data?: {
    created_at?: number,
    lang?: string,
    ref?: string,
    text: string,
    user: {
      name?: string,
      profile_image_url?: string,
      screen_name?: string,
      verified?: boolean
    },
    twitterref?: string
  },
  type: AssetType.Tweet,
  parsed?: boolean
}

export interface WidgetAsset extends Asset {
  url: string,
  reloadKey?: string
}

export interface NewAssetRequest {
  name: string,
  team_id: string,
  cast_id?: string,
  parent_id?: string,
  remote_url?: string,
  type: AssetGroupType,
}

export interface PollAnswer {
  answer: string,
  percent: number
}

export interface TweetPollAsset extends Asset {
  active?: boolean,
  answers?: PollAnswer[],
  num_votes?: 0,
  question?: string,
  timeleft?: string,
  url?: string,
  type: AssetType.TweetPoll,
  up_to_date?: boolean
}

export interface InstagramAssetData {
  created_at: number,
  id: string,
  media_url: string,
  ref: string,
  text: string,
  type: AssetType.Instagram
  url: string
  user?: InstagramUser
}

export interface InstagramAsset extends Asset {
  data: string | InstagramAssetData,
  url?: string
  type: AssetType.Instagram,
  thumbnail?: string
}

// Metadata from remote service
export interface LiveChatAssetData {
  id: string,
  created_at: number,
  text: string,
  user: string,
  ref: string // YouTube/FB video ID (view?v=id)
}

export interface LiveChatAsset extends Asset {
  data: string | LiveChatAssetData,
  type: AssetType.YouTubeChat,
}

export interface GraphicAsset extends Asset {
  thumbnail: string,
  url: string,
  type: AssetType.Graphic
}

export enum ClipTranscodingState {
  Done = 'done',
  Error = 'error',
  Transcoding = 'transcoding'
}

export interface VideoAsset extends Asset {
  errors?: string | null,
  metadata?: {
    duration: number
  },
  original_url?: string,
  progress?: number,
  state?: ClipTranscodingState,
  thumbnail?: string,
  url?: string,
  type: AssetType.Video,
  volume?: number,
  muted?: boolean
  auto_play_next?: boolean
}

export interface AssetGroup {
  id: string,
  type?: AssetGroupType,
  cast_id?: string,
  deleted?: boolean,
  name: string,
  team_id?: string,
  parent_id?: string,
  remote_url?: string, // eg YouTube live video chat url
  timestamp?: number,
  scrape?: boolean, // only for hashtags
}

export interface InstagramUser {
  id: string,
  name: string
}
